<template>
  <div
    id="BreakTheWall"
    class="d-flex align-items-center justify-content-center h-100 background"
    :style="backgroundStyle"
  />
</template>

<script>
import * as PIXI from 'pixi.js';
import { mapGetters } from 'vuex';

const Chance = require('chance');

export default {
  name: 'BreakTheWall',
  data() {
    return {
      chance: new Chance(),
      pixiApp: null,
      tiles: [],
      availableTiles: [],
      dropingTiles: [],
      balls: [],
      backgroundSprite: {},
      backgroundTexture: {},
      ballTexture: {},
      foregroundTexture: null,
      ballImageTexture: null,
    };
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize);
  },
  async mounted() {
    await this.createContext();
    this.loadAssets();
    this.$store.watch(
      (_state) => this.breakTheWallConfig.foregroundImage,
      () => {
        this.loadAssets();
      },
    );
    this.$store.watch(
      (_state) => this.breakTheWallConfig.ballUrl,
      () => {
        this.loadAssets();
      },
    );
    this.$store.watch(
      (_state) => this.currentCommand,
      () => {
        this.createForegroundTiles();
        this.setTilesSize();
      },
    );
    this.$store.watch(
      (_state) => this.breakTheWallBalls,
      (newVal, oldVal) => {
        if (!oldVal) return;

        const oldKeys = new Set(Object.keys(oldVal));
        const newKeys = Object.keys(newVal);

        const addedKeys = newKeys.filter((key) => !oldKeys.has(key));

        // if no added keys, send only one ball
        const totalNewBalls =
          addedKeys.length > 0 ?
            addedKeys.reduce((sum, key) => sum + Object.keys(newVal[key]).length, 0) :
            1;

        for (let i = 0; i < totalNewBalls; i += 1) {
          this.addBallRandomPosition();
        }
      },
      { deep: true },
    );
    window.addEventListener('keypress', (e) => {
      if (e.key === 'b') this.addBallRandomPosition();
      if (e.key === 't') {
        for (let i = 0; i < this.availableTiles.length; i = i + 1) this.addBallRandomPosition();
      }
    });
  },
  computed: {
    ...mapGetters(['breakTheWallConfig', 'currentCommand', 'breakTheWallBalls']),
    backgroundStyle() {
      const out = { overflow: 'hidden' };
      if (this.breakTheWallConfig && this.breakTheWallConfig.backgroundImage)
        out.background = `url("${this.breakTheWallConfig.backgroundImage}") no-repeat center center/cover fixed`;
      if (this.breakTheWallConfig && this.breakTheWallConfig.backgroundColor)
        out.backgroundColor = this.breakTheWallConfig.backgroundColor.hex8 || 'white';
      return out;
    },
  },
  methods: {
    onResize() {
      if (!this.pixiApp) return;
      const elem = document.getElementById('BreakTheWall');
      this.pixiApp.renderer.resize(elem.offsetWidth, elem.offsetHeight);
      this.setTilesSize();
    },
    getTileAt(gridX, gridY) {
      return this.tiles[gridX * this.breakTheWallConfig.gridSize.height + gridY];
    },
    async createContext() {
      const elem = document.getElementById('BreakTheWall');

      this.pixiApp = new PIXI.Application();
      await this.pixiApp.init({
        width: elem.offsetWidth,
        height: elem.offsetHeight,
        antialias: true,
        backgroundAlpha: 0,
        resolution: 1,
      });

      console.log(
        `created WebGl context h:${this.pixiApp.renderer.height} w:${this.pixiApp.renderer.width}`,
      );
      elem.appendChild(this.pixiApp.canvas);
    },
    async loadAssets() {
      if (!this.breakTheWallConfig.foregroundImage || !this.breakTheWallConfig.ballUrl) return;
      try {
        // Charger les textures avec la nouvelle API
        this.foregroundTexture = await PIXI.Assets.load(this.breakTheWallConfig.foregroundImage);
        this.ballImageTexture = await PIXI.Assets.load(this.breakTheWallConfig.ballUrl);
        this.createForegroundTiles();
        this.setTilesSize();
        this.pixiApp.ticker.add((ticker) => this.mainLoopTick(ticker.deltaTime));
      } catch (error) {
        console.error('Error loading assets:', error);
      }
    },
    mainLoopTick(delta) {
      this.tiles.forEach((val) => val.update(delta, val));
      this.dropingTiles.forEach((val) => val.update(delta, val));
      this.balls.forEach((val) => val.update(delta, val));
      this.cleanBalls();
    },
    createForegroundTiles() {
      if (!this.foregroundTexture) return;
      this.pixiApp.stage.removeChildren();
      this.tiles = [];
      this.availableTiles = [];
      for (let x = 0; x < this.breakTheWallConfig.gridSize.width; x += 1)
        for (let y = 0; y < this.breakTheWallConfig.gridSize.height; y += 1) {
          const tile = {};
          tile.gridPosition = {};
          tile.gridPosition.x = x;
          tile.gridPosition.y = y;

          // Calculer les dimensions de chaque portion
          const sprHeight =
            (this.foregroundTexture.height - 1) / this.breakTheWallConfig.gridSize.height;
          const sprWidth =
            (this.foregroundTexture.width - 1) / this.breakTheWallConfig.gridSize.width;

          // Créer un rectangle pour cette portion spécifique de l'image
          const rectangle = new PIXI.Rectangle(x * sprWidth, y * sprHeight, sprWidth, sprHeight);

          // Créer une texture avec cette portion spécifique
          tile.texture = new PIXI.Texture({
            source: this.foregroundTexture.source,
            frame: rectangle,
          });
          tile.sprite = new PIXI.Sprite(tile.texture);
          tile.update = this.tileUpdate;
          tile.destroyed = false;
          tile.destroy = (thisTile) => {
            thisTile.sprite.visible = false;
            thisTile.destroyed = true;
            if (this.breakTheWallConfig.tileDrop) this.addDropingTile(thisTile);
          };
          if (this.breakTheWallConfig.endBehavior === 'reloadFade') tile.sprite.alpha = 0;
          this.availableTiles.push(tile.gridPosition);
          this.tiles.push(tile);
          this.pixiApp.stage.addChild(tile.sprite);
        }
    },
    setTilesSize() {
      if (!this.foregroundTexture) return;
      const tileHeight = this.pixiApp.renderer.height / this.breakTheWallConfig.gridSize.height;
      const tileWidth = this.pixiApp.renderer.width / this.breakTheWallConfig.gridSize.width;

      console.log(`Tiles display size h:${tileHeight} w:${tileWidth}`);
      this.tiles.forEach((tile) => {
        // Définir la taille d'affichage et la position de chaque tile
        tile.sprite.height = tileHeight;
        tile.sprite.width = tileWidth;
        tile.sprite.x = tile.gridPosition.x * tileWidth;
        tile.sprite.y = tile.gridPosition.y * tileHeight;
      });
    },
    addDropingTile(originalTile) {
      const tile = {};
      tile.gridPosition = originalTile.gridPosition;
      // Créer une nouvelle texture avec la même portion que la tile originale
      const sprHeight =
        (this.foregroundTexture.height - 1) / this.breakTheWallConfig.gridSize.height;
      const sprWidth = (this.foregroundTexture.width - 1) / this.breakTheWallConfig.gridSize.width;

      const rectangle = new PIXI.Rectangle(
        tile.gridPosition.x * sprWidth,
        tile.gridPosition.y * sprHeight,
        sprWidth,
        sprHeight,
      );

      tile.texture = new PIXI.Texture({
        source: this.foregroundTexture.source,
        frame: rectangle,
      });

      tile.sprite = new PIXI.Sprite(tile.texture);
      tile.update = this.dropingTileUpdate;
      tile.dropSpeed = this.chance.integer({ min: 6, max: 9 });
      tile.rotationSpeed = this.chance.integer({ min: 3, max: 9 });
      tile.clockWise = this.chance.bool();
      tile.destroy = (thisTile) => {
        thisTile.sprite.visible = false;
        thisTile.destroyed = true;
      };
      const tileHeight = this.pixiApp.renderer.height / this.breakTheWallConfig.gridSize.height;
      const tileWidth = this.pixiApp.renderer.width / this.breakTheWallConfig.gridSize.width;

      tile.rectangle = new PIXI.Rectangle(
        tile.gridPosition.x * sprWidth,
        tile.gridPosition.y * sprHeight,
        sprWidth,
        sprHeight,
      );
      tile.texture.frame = tile.rectangle;
      tile.sprite.height = tileHeight;
      tile.sprite.width = tileWidth;
      tile.sprite.x = tile.gridPosition.x * tileWidth;
      tile.sprite.y = tile.gridPosition.y * tileHeight;
      tile.sprite.pivot.x = tile.sprite.width / 2;
      tile.sprite.pivot.y = tile.sprite.height / 2;
      this.dropingTiles.push(tile);
      this.pixiApp.stage.addChild(tile.sprite);
    },
    addBallRandomPosition() {
      if (this.availableTiles.length <= 0 || this.balls.length >= this.breakTheWallConfig.MaxBalls)
        return;
      const tIDx = this.chance.integer({ min: 0, max: this.availableTiles.length - 1 });
      this.addBall(this.availableTiles[tIDx].x, this.availableTiles[tIDx].y);
      this.removeFromAvailableTiles(this.availableTiles[tIDx]);
      if (this.availableTiles.length <= 0) this.endWall();
    },
    addBall(gridTargetX, gridTargetY) {
      if (!this.ballImageTexture) return;
      const tileHeight = this.pixiApp.renderer.height / this.breakTheWallConfig.gridSize.height;
      const tileWidth = this.pixiApp.renderer.width / this.breakTheWallConfig.gridSize.width;
      const ball = {};
      ball.sprite = new PIXI.Sprite(this.ballImageTexture);

      ball.sprite.scale.x = 0.5;
      ball.sprite.scale.y = 0.5;
      ball.target = {};
      this.setBallSize(ball);
      ball.target.gridX = gridTargetX;
      ball.target.gridY = gridTargetY;
      ball.target.x =
        tileWidth * gridTargetX +
        tileWidth / 2 -
        (this.ballImageTexture.width * ball.target.finalScale) / 2;
      ball.target.y =
        tileHeight * gridTargetY +
        tileHeight / 2 -
        (this.ballImageTexture.height * ball.target.finalScale) / 2;

      ball.sprite.x = this.chance.integer({
        min: this.pixiApp.renderer.width * 0.25,
        max: this.pixiApp.renderer.width * 0.75,
      }); // (this.pixiApp.renderer.width / 2) - (ball.sprite.width / 2);
      ball.sprite.y = this.pixiApp.renderer.height - 10;
      ball.rotationSpeed = this.chance.integer({ min: 3, max: 9 });
      ball.sprite.pivot.x = ball.sprite.width / 2;
      ball.sprite.pivot.y = ball.sprite.height / 2;

      ball.step = {
        x: (ball.target.x - ball.sprite.x) / this.breakTheWallConfig.ballSpeed,
        y: (ball.target.y - ball.sprite.y) / this.breakTheWallConfig.ballSpeed,
        scale: (ball.sprite.scale.x - ball.target.finalScale) / this.breakTheWallConfig.ballSpeed,
      };
      ball.destroyed = false;
      ball.destroy = (thisBall) => {
        thisBall.sprite.visible = false;
        thisBall.destroyed = true;
      };
      ball.update = this.ballUpdate;
      this.balls.push(ball);
      this.pixiApp.stage.addChild(ball.sprite);
    },
    setBallSize(ball) {
      ball.sprite.scale.x = this.breakTheWallConfig.ballScale.start / 100;
      ball.sprite.scale.y = this.breakTheWallConfig.ballScale.start / 100;

      ball.target.finalScale = this.breakTheWallConfig.ballScale.end / 100;
    },
    ballUpdate(delta, ball) {
      if (ball.destroyed) return;
      ball.sprite.x += ball.step.x;
      ball.sprite.y += ball.step.y;
      ball.sprite.scale.x -= ball.step.scale;
      ball.sprite.scale.y -= ball.step.scale;
      ball.sprite.angle += ball.rotationSpeed;
      if (ball.sprite.scale.x <= ball.target.finalScale) {
        ball.destroy(ball);
        const tile = this.getTileAt(ball.target.gridX, ball.target.gridY);
        tile.destroy(tile);
      }
    },
    cleanBalls() {
      this.balls.forEach((ball) => {
        if (ball.destroyed) {
          this.balls.splice(this.balls.indexOf(ball), 1);
          this.pixiApp.stage.removeChild(ball.sprite);
        }
      });
    },
    removeFromAvailableTiles(tile) {
      this.availableTiles.forEach((at) => {
        if (at.x === tile.x && at.y === tile.y)
          this.availableTiles.splice(this.availableTiles.indexOf(at), 1);
      });
    },
    tileUpdate(delta, tile) {
      if (tile.destroyed) return;
      if (tile.sprite.alpha < 1) tile.sprite.alpha += 0.05;
    },
    dropingTileUpdate(delta, tile) {
      tile.sprite.y += tile.dropSpeed;
      if (tile.clockWise) tile.sprite.angle += tile.rotationSpeed;
      else tile.sprite.angle -= tile.rotationSpeed;
    },
    endWall() {
      console.log('End of the wall');
      if (this.breakTheWallConfig.endBehavior !== 'nothing') {
        console.log(
          `Wall reload in ${this.breakTheWallConfig.timeBeforeFade * 1000}ms with ${
            this.breakTheWallConfig.endBehavior
          }`,
        );
        setTimeout(() => {
          this.createForegroundTiles();
          this.setTilesSize();
        }, this.breakTheWallConfig.timeBeforeFade * 1000);
      }
    },
  },
};
</script>

<style scoped></style>
